import { Hotspot } from "@nova/protobuf"
import { generateGenericCases } from "../../generateCases"
import { Builder } from "../../sockets"
import {
    CreateHotspotArgs,
    getDefaultHotspotCreateArgs,
    UpdateHotspotArgs,
} from "@nova/serializer/src/client/validators/cubes/hotspot"
import {
    addEntitySocket,
    batchUpdateEntitiesSocket,
    deleteEntitySocket,
    updateEntitySocket,
} from "features/entity-caching/project/slice"
import { createAction as createReduxAction } from "@reduxjs/toolkit"
import { v4 as randomUUID } from "uuid"

const {
    create: createHotspot,
    update: updateHotspot,
    delete: deleteHotspot,
    createAction,
    updateAction,
    deleteAction,
} = generateGenericCases<
    Hotspot, // entity type
    CreateHotspotArgs, // create args to send to server
    ["longitude", "latitude"], // create args required by action
    UpdateHotspotArgs, // update args
    { flightId: string } // path required to find entity
>("Hotspot")

const batchUpdateHotspotsAction = createReduxAction<{
    ids: string[]
    updates: Partial<Pick<Hotspot, UpdateHotspotArgs[number]>>
    context: { flightId: string }
}>("batchUpdateHotspots")

export const addHotspotsToBuilder = (builder: Builder) =>
    builder
        .addCase(createAction, (store, { payload: { data, context } }, socket) => {
            const args = { ...data, confidence: 1, polygon: [], imageId: "mock" }
            const optimisticData = { ...args, id: randomUUID(), ...getDefaultHotspotCreateArgs() }
            createHotspot(socket, { ...args, id: optimisticData.id }, context)
            store.dispatch(
                addEntitySocket({
                    data: optimisticData,
                    path: ["infraredFlights", { id: context.flightId }, "hotspots"],
                })
            )
        })
        .addCase(updateAction, (store, { payload: { id, updates, context } }, socket) => {
            updateHotspot(socket, id, updates, context)
            store.dispatch(
                updateEntitySocket({
                    updates,
                    path: ["infraredFlights", { id: context.flightId }, "hotspots", { id }],
                })
            )
        })
        .addCase(deleteAction, (store, { payload: { id, context } }, socket) => {
            deleteHotspot(socket, id, context)
            store.dispatch(
                deleteEntitySocket({
                    path: ["infraredFlights", { id: context.flightId }, "hotspots", { id }],
                })
            )
        })
        .addCase(
            batchUpdateHotspotsAction,
            (store, { payload: { ids, updates, context } }, socket) => {
                socket.emit("batchUpdateHotspot", ids, updates, context)
                store.dispatch(
                    batchUpdateEntitiesSocket({
                        ids,
                        updates,
                        path: ["infraredFlights", { id: context.flightId }, "hotspots"],
                    })
                )
            }
        )

export {
    createAction as createHotspot,
    updateAction as updateHotspot,
    deleteAction as deleteHotspot,
    batchUpdateHotspotsAction,
}
